import { FC, useCallback, useState } from 'react';

import { ExclamationIcon } from '@heroicons/react/outline';

import { SHARE_GUIDE_DISMISSED_KEY } from '../utils/giftOrderCoupon.utils';

interface ShareGuideModalProps {
  open: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export const ShareGuideModal: FC<ShareGuideModalProps> = ({
  open,
  onConfirm,
  onClose,
}) => {
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleConfirm = useCallback(() => {
    if (!confirmed) return;
    if (dontShowAgain) {
      localStorage.setItem(SHARE_GUIDE_DISMISSED_KEY, 'true');
    }
    onConfirm();
  }, [confirmed, dontShowAgain, onConfirm]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-5">
      <div className="w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-xl">
        {/* 헤더 */}
        <div className="flex items-center gap-2 border-b border-gray-100 px-5 py-3.5">
          <ExclamationIcon className="h-5 w-5 shrink-0 text-gray-500" />
          <h3 className="text-base font-bold text-gray-800">전달 전 확인해 주세요</h3>
        </div>

        <div className="px-5 pb-5 pt-4">
          {/* 안내 본문 */}
          <p className="mb-3 break-keep text-sm leading-6 text-gray-700">
            답례품{' '}
            <span className="inline-block whitespace-nowrap font-bold text-red-600">
              한 개를 여러 분께
            </span>{' '}
            공유하실 경우{' '}
            <span className="inline-block whitespace-nowrap font-bold text-red-600">
              중복 발송
            </span>
            으로 문제가 발생할 수 있습니다.
          </p>

          {/* 핵심 안내 */}
          <div className="mb-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3 text-center">
            <p className="break-keep text-base font-bold text-gray-800">
              <span className="inline-block whitespace-nowrap text-red-600">
                한 개당 한 분씩
              </span>{' '}
              <span className="inline-block whitespace-nowrap">전달해 주세요.</span>
            </p>
          </div>

          {/* 확인 체크박스 */}
          <label className="mb-3 flex cursor-pointer select-none items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2.5 active:bg-gray-50">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => setConfirmed(e.target.checked)}
              className="h-5 w-5 shrink-0 rounded border-gray-300 text-gray-700 focus:ring-gray-400"
            />
            <span className="break-keep text-sm font-medium text-gray-800">
              위 안내를 확인했습니다
            </span>
          </label>

          {/* 다시 보지 않기 */}
          <label className="mb-4 flex cursor-pointer items-center gap-2 pl-1">
            <input
              type="checkbox"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-gray-600 focus:ring-gray-400"
            />
            <span className="text-xs text-gray-500">다시 보지 않기</span>
          </label>

          {/* 버튼 */}
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 rounded-lg border border-gray-300 py-2.5 text-sm font-medium text-gray-600 active:bg-gray-50"
            >
              취소
            </button>
            <button
              onClick={handleConfirm}
              disabled={!confirmed}
              className={`flex-1 rounded-lg py-2.5 text-sm font-bold text-white transition ${
                confirmed
                  ? 'bg-gray-800 active:bg-gray-900'
                  : 'cursor-not-allowed bg-gray-300'
              }`}
            >
              확인
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
